
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AdminAuthContextType {
  isAdmin: boolean;
  adminUser: User | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

// Create the context with default values
const AdminAuthContext = createContext<AdminAuthContextType>({
  isAdmin: false,
  adminUser: null,
  isLoading: true,
  signIn: async () => {},
  signOut: async () => {},
});

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [adminUser, setAdminUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkAdminStatus = async (userId: string | undefined) => {
    if (!userId) {
      console.log('No user ID provided for admin check');
      setIsAdmin(false);
      setIsLoading(false);
      return;
    }

    try {
      console.log('Checking admin status for user:', userId);
      
      // Use the new RLS-safe function that bypasses policies
      const { data: isUserAdmin, error: adminError } = await supabase
        .rpc('is_admin_bypass_rls');

      if (adminError) {
        console.error('Admin check failed:', adminError);
        // Don't fail completely, just set as non-admin
        setIsAdmin(false);
      } else {
        const adminStatus = !!isUserAdmin;
        console.log('Admin check successful - user is admin:', adminStatus);
        setIsAdmin(adminStatus);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      // Don't fail completely, just set as non-admin
      setIsAdmin(false);
    }
    
    console.log('Setting isLoading to false');
    setIsLoading(false);
  };

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        // Get current session
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting session:', error);
          if (mounted) {
            setAdminUser(null);
            setIsAdmin(false);
            setIsLoading(false);
          }
          return;
        }

        if (mounted) {
          console.log('Initial session check:', session?.user?.id || 'No session');
          setAdminUser(session?.user ?? null);
          if (session?.user) {
            await checkAdminStatus(session.user.id);
          } else {
            setIsAdmin(false);
            setIsLoading(false);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        if (mounted) {
          setAdminUser(null);
          setIsAdmin(false);
          setIsLoading(false);
        }
      }
    };

    // Initialize auth state
    initializeAuth();

    // Listen for auth changes - CRITICAL: Use setTimeout to prevent deadlock
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth state changed:', event, session?.user?.id || 'No user');
      
      if (mounted) {
        setAdminUser(session?.user ?? null);
        
        if (session?.user && event !== 'SIGNED_OUT') {
          // Use setTimeout to defer the async call and prevent callback deadlock
          setTimeout(() => {
            if (mounted) {
              checkAdminStatus(session.user.id);
            }
          }, 0);
        } else {
          setIsAdmin(false);
          setIsLoading(false);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    console.log('Attempting to sign in...');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      console.error('Sign in error:', error);
      throw error;
    }
    console.log('Sign in successful');
  };

  const signOut = async () => {
    try {
      console.log('Signing out...');
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Logout error:', error);
        throw error;
      }
      
      // Clear local state immediately
      setAdminUser(null);
      setIsAdmin(false);
      console.log('Logout successful');
      
      // Use window.location for immediate redirect to avoid router issues
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  };

  return (
    <AdminAuthContext.Provider value={{ isAdmin, adminUser, isLoading, signIn, signOut }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};
